import type { Role, User } from '@conversia/shared-types';

/**
 * USUARIOS · buscador, filtros y el selector de rol. PURO.
 *
 * La pantalla pedía los 100 usuarios y los pintaba todos, sin buscador, sin
 * filtros y sin decir cuántos eran. Con veinte cuentas encontrar a alguien ya
 * es leer la lista entera.
 *
 * Vive acá y no dentro del componente por dos razones:
 *
 *  1. Son decisiones que se pueden probar: qué cuenta como coincidencia, qué
 *     opciones ofrece cada selector y qué se le dice a alguien antes de
 *     cambiarle el rol.
 *  2. **El caso del Dueño era un bug visible.** El selector de rol de cada fila
 *     tenía cuatro opciones y ninguna era OWNER, así que la fila del dueño se
 *     pintaba con la primera de la lista: parecía que el dueño era
 *     Administrador. Ahora la opción existe, dice «Dueño del sistema» y no se
 *     puede elegir — darla desde el panel nunca fue la intención.
 */

/** Una opción de un `<Select>`. Se declara acá para no importar el componente. */
export interface OpcionDeSelect {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Las opciones del selector de rol de cada fila.
 *
 * El Dueño entra DESACTIVADO: se muestra para que la fila del dueño diga la
 * verdad, y no se puede elegir porque esa cuenta no se reparte desde una
 * pantalla de listado.
 */
export const OPCIONES_DE_ROL: OpcionDeSelect[] = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'AGENT', label: 'Agente' },
  { value: 'VIEWER', label: 'Lector' },
  { value: 'TECNICO', label: 'Técnico' },
  { value: 'OWNER', label: 'Dueño del sistema', disabled: true },
];

/**
 * Qué puede hacer cada rol, en UNA línea.
 *
 * Es lo que se lee antes de confirmar el cambio: cambiarle el rol a alguien es
 * abrirle o cerrarle puertas, y hasta ahora se aplicaba al soltar el selector,
 * sin preguntar nada.
 */
const QUE_HACE: Record<Role, string> = {
  OWNER: 'Ve todo, incluida la plata y los topes de gasto.',
  ADMIN: 'Maneja todo el día a día: trabajos, despachos, clientes y el equipo.',
  AGENT: 'Atiende las conversaciones y el embudo de ventas, sin tocar los ajustes.',
  VIEWER: 'Solo mira: no puede cambiar ni cerrar nada.',
  TECNICO: 'Entra desde el celular y solo ve los trabajos que le tocan a él.',
};

/** La línea que explica el rol nuevo. PURO. */
export function queHaceElRol(rol: Role): string {
  return QUE_HACE[rol];
}

/** Opciones del filtro de rol. El vacío es "sin filtrar". */
export const OPCIONES_FILTRO_ROL: OpcionDeSelect[] = [
  { value: '', label: 'Todos los roles' },
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'AGENT', label: 'Agente' },
  { value: 'VIEWER', label: 'Lector' },
  { value: 'TECNICO', label: 'Técnico' },
  // Acá sí entra el Dueño: filtrar por él es mirar, no repartir la cuenta.
  { value: 'OWNER', label: 'Dueño' },
];

export type EstadoUsuarios = 'todos' | 'activos' | 'inactivos';

/** Opciones del filtro de activos. El primero es "sin filtrar". */
export const OPCIONES_FILTRO_ESTADO: OpcionDeSelect[] = [
  { value: 'todos', label: 'Activos e inactivos' },
  { value: 'activos', label: 'Solo activos' },
  { value: 'inactivos', label: 'Solo inactivos' },
];

export interface FiltrosDeUsuarios {
  busqueda?: string;
  rol?: string;
  estado?: EstadoUsuarios;
}

/** Minúsculas y sin tildes: "GÓMEZ" tiene que encontrar a "Gómez". */
function normalizar(texto: string): string {
  return texto.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Solo los dígitos: nadie copia el teléfono con los espacios de la ficha. */
function soloNumeros(texto: string): string {
  return texto.replace(/\D/g, '');
}

/**
 * Los usuarios que quedan después del buscador y los dos filtros. PURO.
 *
 * Conserva el orden en que vinieron del servidor: reordenar acá haría que la
 * lista saltara mientras se escribe en el buscador.
 */
export function filtrarUsuarios(usuarios: User[], filtros: FiltrosDeUsuarios): User[] {
  const texto = normalizar((filtros.busqueda ?? '').trim());
  const digitos = soloNumeros(filtros.busqueda ?? '');
  const rol = filtros.rol?.trim() || null;
  const estado = filtros.estado ?? 'todos';

  return usuarios.filter((u) => {
    if (rol && u.role !== rol) return false;
    if (estado === 'activos' && !u.isActive) return false;
    if (estado === 'inactivos' && u.isActive) return false;
    if (!texto) return true;

    const campos = normalizar(`${u.name ?? ''} ${u.email}`);
    if (campos.includes(texto)) return true;
    // El teléfono se compara por dígitos, no por el texto tal cual se guardó.
    return Boolean(digitos) && soloNumeros(u.telefono ?? '').includes(digitos);
  });
}

/** "12 usuarios" / "1 usuario". PURO. */
export function contadorDeUsuarios(n: number): string {
  return `${n} ${n === 1 ? 'usuario' : 'usuarios'}`;
}
